// ============================================================================
// EXTEND INTEGRATION
// ============================================================================
// available during synchronous execution, not inside async callbacks.
const _scriptEl = document.currentScript;

// ============================================================================
// LOADERS
// ============================================================================

function loadScript(src) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Extend: failed to load script: ${src}`));
        document.head.appendChild(script);
    });
}

async function loadExtendSDKs() {
    await loadScript('https://sdk.helloextend.com/extend-sdk-client/v1/extend-sdk-client.min.js');
    await loadScript('https://sdk.helloextend.com/extend-sdk-client-shopify-addon/v1/extend-sdk-client-shopify-addon.min.js');
}

// ============================================================================
// GLOBAL VARIABLES & CONFIGURATION
// ============================================================================

// SKILL - Replace with correct store ID
const storeId = 'adf9201c-3004-4e12-8f08-2cafc1fd07aa'; // Extend Admin or Merchant Portal
const env = 'production'; // 'production' or 'dev'
const isMainCart = location.pathname.includes('/cart');
let cart = null; // populated by fetchCart(), shared across all snippets

// ============================================================================
// SHARED UTILITIES
// ============================================================================

const mkEl = (tag, cls) => {
    const el = document.createElement(tag);
    if (cls) el.className = cls;
    return el;
};

async function fetchCart() {
    const res = await fetch('/cart.js');
    cart = await res.json();
    return cart;
}

// ============================================================================
// INITIALIZATION
// ============================================================================

function initExtendConfiguration() {
    if (!window.Extend || !window.ExtendShopify) return;

    Extend.config({ storeId, environment: env });

    Extend.integration = {
        aftermarketModal: true,
        currency: 'USD',
        pdpOffer: true,
        pdpModalOffer: true,
        offerProperties: {},
        cartOffer: true,
        sidecartOffer: false,
        cartBalancing: true,
        atcOffer: true,
    };

    if (Shopify?.currency?.active !== Extend.integration.currency) return;

    Extend.integration.aftermarketModal && initAftermarket();
    Extend.integration.pdpOffer && initProductPageOffer();
    Extend.integration.atcOffer && initATCOffer();

    const needsCart = Extend.integration.cartOffer;
    if (needsCart) {
        fetchCart()
            .then(() => {
                Extend.integration.cartOffer && initCartOffer(cart);
            })
            .catch(err => console.error('Extend: could not fetch cart:', err));
    }
}

// ============================================================================
// BOOT
// ============================================================================

loadExtendSDKs()
    .then(initExtendConfiguration)
    .catch(err => console.error('Extend: SDK failed to load:', err));

// ============================================================================
// AFTERMARKET
// ============================================================================

function initAftermarket() {
    try {
        const params = new URL(document.location).searchParams;
        const leadToken = params.get('leadToken') ?? params.get('leadtoken');
        if (!leadToken) return;

        Extend.aftermarketModal.open({
            leadToken,
            onClose(plan, product, quantity) {
                if (plan && product) {
                    ExtendShopify.addPlanToCart(
                        { plan, product, leadToken, quantity: quantity || 1 },
                        () => {
                            location.href = '/cart';
                        }
                    );
                }
            },
        });
    } catch (err) {
        console.error('EXTEND - AFTERMARKET:', err);
    }
}

// ============================================================================
// PDP OFFER
// ============================================================================

function initProductPageOffer() {
    if (meta?.page?.pageType !== 'product') return;

    // SKILL - Replace with correct form element, closest form parent to the add to cart button on a product page
    const productForm = document.querySelector('[data-product-form]'); // THEME SPECIFIC
    if (!productForm) return;

    // SKILL - Replace with correct button element, sometimes not a button, this is the main add to cart
    // THEME SPECIFIC: this theme sometimes swaps in a SECOND, otherwise-hidden
    // add-to-cart button (#btn-addcart-smg, inside .container-for-new-upsells)
    // and hides the standard [data-add-to-cart] button instead — this happens
    // on DOMContentLoaded for "megatab"/bundle product selectors, and that
    // second button is wired (with its own ~2s-delayed listener) to submit the
    // form directly, bypassing whichever listener is bound only to the other
    // button. So Extend delegates the click listener from productForm itself
    // and matches against both known button selectors, rather than binding to
    // a single button reference up front.
    const addToCartSelector = '[data-add-to-cart], #btn-addcart-smg';
    if (!productForm.querySelector(addToCartSelector)) return;

    const dispatchSideCart = false;
    const productCategory = meta?.product?.type ?? null;

    const extendOffer = mkEl('div', 'extend-offer');
    extendOffer.style.margin = "-10px 0 10px 0"
    productForm.prepend(extendOffer);

    function getVariantPrice(variantId) {
        const match = meta?.product?.variants?.find(v => v.id.toString() === variantId.toString());
        return match ? parseInt(match.price, 10) : null;
    }

    async function getOfferProps(variantId, price) {
        const { components } = (await ExtendShopify.getBundleComponents({
            variantId: `gid://shopify/ProductVariant/${variantId}`,
        })) || {};

        return components?.length > 0
            ? { referenceId: variantId, products: components }
            : { referenceId: variantId, price, category: productCategory };
    }

    async function initProductOffer() {
        try {
            if (meta?.product?.vendor === 'Extend') {
                productForm.querySelectorAll(addToCartSelector).forEach(btn => (btn.disabled = true));
            }

            let variantId = productForm.querySelector('[name="id"]')?.value;
            let price = getVariantPrice(variantId);

            Extend.integration.offerProperties = { variant: variantId, category: productCategory, price };

            Extend.buttons.render(extendOffer, await getOfferProps(variantId, price));

            productForm.addEventListener('change', async function () {
                try {
                    variantId = productForm.querySelector('[name="id"]')?.value;
                    if (!variantId) return;

                    price = getVariantPrice(variantId);
                    Extend.integration.offerProperties = { variant: variantId, category: productCategory, price };
                    Extend.setActiveProduct(extendOffer, await getOfferProps(variantId, price));
                } catch (err) {
                    console.error('EXTEND - VARIANT CHANGE:', err);
                }
            });

            // THEME SPECIFIC: delegate from productForm rather than binding to a
            // single button reference. On this theme the theme's own click handler
            // for the active add-to-cart button can attach itself well after page
            // load (up to ~2s, via a custom element's connectedCallback + internal
            // setTimeout), and which button is even visible can change on
            // DOMContentLoaded depending on a bundle/upsell selector's state. A
            // delegated capture-phase listener on the form catches the real click
            // regardless of which button element receives it or when the theme's
            // own listener gets wired up, and intercepts before that listener runs.
            function handleAddToCart(e) {
                const clickedButton = e.target.closest(addToCartSelector);
                if (!clickedButton || !productForm.contains(clickedButton)) return;

                e.preventDefault();
                e.stopImmediatePropagation();

                const quantity = productForm.querySelector('[name="quantity"]')?.value || 1;

                ExtendShopify.handleAddToCart(extendOffer, {
                    quantity,
                    modal: Extend.integration.pdpModalOffer,
                    done() {
                        productForm.removeEventListener('click', handleAddToCart, true);
                        clickedButton.click();
                        productForm.addEventListener('click', handleAddToCart, true);
                        if (dispatchSideCart) {
                            // THEME SPECIFIC: the click above kicks off this theme's own
                            // async add-to-cart chain (POST /cart/add.js, then GET
                            // /cart.js, then dispatch 'theme:cart:change') before the
                            // product is actually confirmed in the cart. Firing
                            // 'refreshAjaxSideCart' on a flat timer races that chain —
                            // if the product hasn't landed yet when Extend re-pairs the
                            // warranty with the cart, the warranty can look orphaned and
                            // get dropped. Wait for the theme's own confirmation event
                            // instead, with the old timeout kept only as a fallback in
                            // case that event doesn't fire for some reason.
                            let dispatched = false;
                            const dispatchOnce = () => {
                                if (dispatched) return;
                                dispatched = true;
                                document.removeEventListener('theme:cart:change', dispatchOnce);
                                window.dispatchEvent(new Event('refreshAjaxSideCart'));
                            };
                            document.addEventListener('theme:cart:change', dispatchOnce);
                            setTimeout(dispatchOnce, 2500);
                        }
                    },
                });
            }

            productForm.addEventListener('click', handleAddToCart, true);
        } catch (err) {
            console.error('EXTEND - PRODUCT OFFER:', err);
        }
    }

    if (!Extend.buttons.instance(extendOffer)) initProductOffer();
}

// ============================================================================
// ATC OFFER — THEME SPECIFIC
// ============================================================================

function initATCOffer() {
    const atcSelector = 'button[data-quick-buy]';
    const dispatchSideCart = false;

    function runATC(e) {
        if (!e.target?.closest(atcSelector)) return;

        const el = e.target.closest(atcSelector);
        e.preventDefault();
        e.stopImmediatePropagation();
        document.removeEventListener('click', runATC, true);

        const variantId = el.getAttribute('data-variant-id');

        let productCategory;
        let productPrice;

        Extend.modal.open({
            referenceId: variantId,
            price: productPrice,
            category: productCategory,
            onClose(plan, product) {
                if (plan && product) {
                    ExtendShopify.addPlanToCart({ plan, product, quantity: 1 }, err => {
                        if (err) return;
                    });
                }
                el.click();
                document.addEventListener('click', runATC, true);
                if (dispatchSideCart) {
                    setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 500);
                }
            },
        });
    }

    document.addEventListener('click', runATC, true);
}

// ============================================================================
// CART OFFER
// ============================================================================

function initCartOffer(initialCart) {
    if (!isMainCart && !Extend.integration.sidecartOffer) return;

    cart = initialCart;
    let init = false;

    // SKILL - Replace with container of each cart item selector
    const cartRowItem = '[data-cart-item]'; // THEME SPECIFIC - identical markup for main cart & drawer (both render snippets/cart-line-items.liquid)
    // SKILL - Replace with cart row item image outermost element with a link selector
    const cartRowItemImage = '.cart__items__image a'; // THEME SPECIFIC
    // SKILL - Replace with cart row item title selector
    const cartRowItemTitle = '.cart__items__title a'; // THEME SPECIFIC
    // SKILL - Replace with cart row item quantity input selector
    const cartRowItemQuantity = '[data-quantity-input]'; // THEME SPECIFIC
    // SKILL - Replace with cart row item metadata display elements selector
    const warrantyOriginalMeta = '.cart__items__meta'; // THEME SPECIFIC
    // SKILL - Replace with cart row item product info container selector
    const warrantyContainer = '.cart__items__meta'; // THEME SPECIFIC
    // SKILL - Replace with cart row item product info container selector
    const metadataContainer = '.cart__items__title'; // THEME SPECIFIC
    // SKILL - Replace with cart row item quantity wrapper selector
    const quantityWrapper = '[data-quantity-selector]'; // THEME SPECIFIC
    const offerClass = isMainCart ? 'extend-cart-offer' : 'extend-side-cart-offer';
    const cartEvent = isMainCart ? 'refreshAjaxCart' : 'refreshAjaxSideCart';
    const regEx = /\d+$/;

    // THEME SPECIFIC: the actual cart rows live inside this container on BOTH
    // the standalone /cart page (sections/cart.liquid) and the side drawer
    // (snippets/cart-drawer.liquid) — it's the element the theme's own JS
    // replaces wholesale (via .innerHTML=) once an add/update/remove finishes.
    // Any listeners the SDK attached to the old row elements go with them
    // when that happens.
    const lineItemsContainer = document.querySelector('[data-line-items]');

    async function refreshCart() {
        location.reload();
    }

    async function renderCartOffer(el, variantId, quantity, index) {
        try {
            if (
                ExtendShopify.warrantyAlreadyInCart(parseInt(variantId, 10), cart.items) ||
                ExtendShopify.warrantyAlreadyInCart(variantId.toString(), cart.items)
            ) {
                return;
            }

            if (isMainCart && !Extend.integration.cartOffer) return;
            if (!isMainCart && !Extend.integration.sidecartOffer) return;

            const productCategory = cart.items[index]?.product_type;
            const productPrice = cart.items[index]?.price;

            const { components } = (await ExtendShopify.getBundleComponents({
                variantId: `gid://shopify/ProductVariant/${variantId}`,
            })) || {};
            const isBundle = components?.length > 0;

            const offerProps = isBundle
                ? { referenceId: variantId, products: components }
                : { referenceId: variantId, price: productPrice, category: productCategory };

            Extend.buttons.renderSimpleOffer(el, {
                ...offerProps,
                onAddToCart(options) {
                    ExtendShopify.addPlanToCart(
                        {
                            plan: options.plan,
                            product: options.product,
                            ...(isBundle && { products: options.products }),
                            quantity,
                        },
                        function (err) {
                            if (err) {
                                console.error('EXTEND - onAddToCart:', err);
                            } else {
                                refreshCart();
                            }
                        }
                    );
                },
            });
        } catch (err) {
            console.error('EXTEND - renderCartOffer:', err);
        }
    }

    function createElement(product, index) {
        const url = product.querySelector(cartRowItemTitle)?.href;
        const variantId = url?.match(regEx)?.[0] ?? cart.items[index]?.id;
        const quantity = parseInt(product.querySelector(cartRowItemQuantity)?.value, 10) || 1;

        if (!variantId || !quantity) {
            throw new Error(`Exiting - invalid variantId (${variantId}) or quantity (${quantity})`);
        }

        const existingOffer = product.querySelector('.' + offerClass);
        if (existingOffer) {
            if (
                existingOffer.dataset.extendVariant !== variantId.toString() ||
                existingOffer.dataset.extendQuantity !== quantity.toString()
            ) {
                existingOffer.remove();
            } else {
                return;
            }
        }

        const container = product.querySelector(warrantyContainer);
        if (!container) throw new Error('Exiting - warrantyContainer unavailable');

        const newOffer = mkEl('div', offerClass);
        newOffer.setAttribute('data-extend-variant', variantId);
        newOffer.setAttribute('data-extend-quantity', quantity);
        container.append(newOffer);

        renderCartOffer(newOffer, variantId, quantity, index);
    }

    function handleStyling() {
        document.querySelectorAll(cartRowItem).forEach(function (el, index) {
            try {
                const title = el.querySelector(cartRowItemTitle);
                if (!title) throw new Error('Exiting - title unavailable');

                if (title.innerText.toLowerCase().includes('extend protection')) {
                    title.style.pointerEvents = 'none';

                    const image = el.querySelector(cartRowItemImage);
                    if (!image) throw new Error('Exiting - image unavailable');
                    image.style.pointerEvents = 'none';

                    el.querySelectorAll(warrantyOriginalMeta).forEach(each => each.remove());

                    if (quantityWrapper) {
                        el.querySelectorAll(quantityWrapper).forEach(each => {
                            each.style.opacity = '75%';
                            each.style.pointerEvents = 'none';
                        });
                    }

                    if (!cart.items[index]) return;

                    const item = cart.items[index];
                    let warrantyProductData;
                    let warrantyTermData;

                    warrantyProductData = item.properties?.Product;
                    warrantyTermData = item.properties?.Term;

                    if (warrantyProductData === 'Covered Product') {
                        const coveredId = item.properties?._Extend?.ProductId || item.properties?.['_Extend.ProductId'];
                        const coveredItem = cart.items.find(i => i.id.toString() === coveredId);
                        warrantyProductData = coveredItem?.title ?? warrantyProductData;
                    }

                    const contentContainer = el.querySelector(metadataContainer);
                    if (contentContainer && !el.querySelector('.extend-warranty-info')) {
                        const productName = mkEl('p', 'extend-warranty-info extend-warranty-info-product');
                        productName.innerHTML = `<b>Product: </b>${warrantyProductData}`;
                        productName.setAttribute('data-cy', 'warranty-description-product');
                        productName.style.color = '#4d4d4d'
                        productName.style.fontSize = '12px'

                        const productTerm = mkEl('p', 'extend-warranty-info extend-warranty-info-term');
                        productTerm.innerHTML = `<b>Term: </b>${warrantyTermData}`;
                        productTerm.setAttribute('data-cy', 'warranty-description-term');
                        productTerm.style.color = '#4d4d4d'
                        productTerm.style.fontSize = '12px'

                        contentContainer.append(productName, productTerm);
                    }
                } else {
                    createElement(el, index);
                }
            } catch (err) {
                console.error('EXTEND:', err);
            }
        });
    }

    // THEME SPECIFIC: re-run handleStyling() whenever the theme actually
    // replaces the cart rows in the DOM, not just when theme:cart:change
    // fires. This theme dispatches that event BEFORE it fetches and swaps in
    // the updated row HTML (the /cart/change.js response triggers fireChange()
    // immediately; the fresh markup arrives later via a separate
    // ?section_id=api-cart-items fetch that replaces [data-line-items].innerHTML
    // wholesale). Two problems follow from that:
    //   1. handleStyling() run off theme:cart:change alone checks the OLD rows
    //      — e.g. right after removing a warranty, its row is still present,
    //      so the covered product's row is never reclassified and never gets
    //      its offer widget back.
    //   2. Once the real swap lands, any click listeners Extend's SDK attached
    //      to the old row elements (for existing offer widgets) are gone with
    //      them, and nothing re-runs handleStyling() against the new elements
    //      to re-attach anything.
    // Watching the container directly catches the actual replacement whenever
    // it happens, independent of the theme's own event/request timing.
    let lineItemsObserver = null;
    let lineItemsDebounce = null;
    function watchLineItems() {
        if (!lineItemsContainer || !('MutationObserver' in window)) return;
        if (lineItemsObserver) lineItemsObserver.disconnect();
        lineItemsObserver = new MutationObserver(() => {
            clearTimeout(lineItemsDebounce);
            lineItemsDebounce = setTimeout(() => {
                // Disconnect while we make our own DOM changes below (offer
                // widgets, warranty info paragraphs) so we don't react to them
                // and loop; reconnect once they've settled.
                lineItemsObserver.disconnect();
                try {
                    handleStyling();
                } catch (err) {
                    console.error('EXTEND:', err);
                } finally {
                    lineItemsObserver.observe(lineItemsContainer, { childList: true, subtree: true });
                }
            }, 120);
        });
        lineItemsObserver.observe(lineItemsContainer, { childList: true, subtree: true });
    }

    function initEventListeners() {
        if (init) return;

        function onCartMutated() {
            fetchCart()
                .then(() => initializeCartOffer())
                .catch(err => console.error('EXTEND:', err));
        }

        // Extend's own cart-refresh trigger. Always listened for — manually
        // dispatchable (`window.dispatchEvent(new CustomEvent('extendCartDispatch'))`)
        // and also fired internally by anything else in this file that mutates the cart.
        window.addEventListener('extendCartDispatch', onCartMutated);

        // SKILL - Piggyback on the merchant's own cart-mutation event(s), if any exist.
        // THEME SPECIFIC: this theme dispatches 'theme:cart:change' on `document`
        // (bubbles: true) from assets/theme.dev.js after every cart add, quantity
        // change, and remove (see pn.fireChange() and product-add-button's
        // updateHeaderTotal()). Listening on `document` here, matching the dispatch target.
        // Note this fires before the theme's own row HTML actually updates —
        // see watchLineItems() above for how that's covered.
        document.addEventListener('theme:cart:change', onCartMutated);
        //
        // Last resort ONLY, if no usable merchant event exists anywhere in the theme:
        // inject a single minimal `window.dispatchEvent(new CustomEvent('extendCartDispatch'))`
        // line directly into the merchant's own cart-mutation code (add/qty/remove/upsell
        // handler — whichever exists). No restructuring of the surrounding function.
        // Any such injection into merchant code MUST be called out explicitly in the
        // integration summary (file, function, line added) — this is the one part of
        // the skill that edits code outside extend.js.

        // Legacy fallback trigger some earlier integrations relied on — kept for
        // backward compatibility, safe to leave even if unused.
        window.addEventListener(cartEvent, onCartMutated);

        window.addEventListener('refreshSP', refreshCart);

        watchLineItems();
    }

    function initializeCartOffer() {
        if (Extend.integration.cartBalancing) {
            ExtendShopify.normalizeCart({ cart, balance: true }, function (err, data) {
                try {
                    if (err) throw new Error('normalizeCart error: ' + err);
                    if (data?.updates) refreshCart();
                } catch (err2) {
                    console.error('EXTEND:', err2);
                }
            });
        }

        handleStyling();
        initEventListeners();
        init = true;
    }

    try {
        initializeCartOffer();
    } catch (err) {
        console.error('EXTEND:', err);
    }
}